import { useState } from 'react';
import { ref, uploadBytes, getDownloadURL, deleteObject, getMetadata } from 'firebase/storage';
import { storage } from '../firebase';

// File upload configurations
export const UPLOAD_CONFIGS = {
  PROFILE_IMAGES: {
    path: 'profileImages',
    maxSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    maxWidth: 800,
    maxHeight: 800
  },
  RESUMES: {
    path: 'resumes',
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
  },
  PORTFOLIOS: {
    path: 'portfolios',
    maxSize: 50 * 1024 * 1024, // 50MB
    allowedTypes: ['application/pdf', 'image/jpeg', 'image/png']
  },
  EVENT_IMAGES: {
    path: 'eventImages',
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    maxWidth: 1200,
    maxHeight: 800
  },
  COMPANY_LOGOS: {
    path: 'companyLogos',
    maxSize: 2 * 1024 * 1024, // 2MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/svg+xml'],
    maxWidth: 400,
    maxHeight: 400
  },
  DOCUMENTS: {
    path: 'documents',
    maxSize: 25 * 1024 * 1024, // 25MB
    allowedTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ]
  }
};

// Validate file before upload
export const validateFile = (file, config) => {
  const errors = [];

  // Check file size
  if (file.size > config.maxSize) {
    errors.push(`File size must be less than ${config.maxSize / (1024 * 1024)}MB`);
  }

  // Check file type
  if (!config.allowedTypes.includes(file.type)) {
    errors.push(`File type ${file.type} is not allowed. Allowed types: ${config.allowedTypes.join(', ')}`);
  }

  return errors;
};

// Resize image if needed
export const resizeImage = (file, maxWidth, maxHeight) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(resolve, file.type, 0.9);
    };

    img.src = URL.createObjectURL(file);
  });
};

// Upload file to Firebase Storage
export const uploadFile = async (file, configKey, userId, customPath = '') => {
  try {
    const config = UPLOAD_CONFIGS[configKey];
    if (!config) {
      throw new Error('Invalid upload configuration');
    }

    // Validate file
    const validationErrors = validateFile(file, config);
    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join(', '));
    }

    let fileToUpload = file;

    // Resize image if it's an image and has size constraints
    if (file.type.startsWith('image/') && config.maxWidth && config.maxHeight) {
      fileToUpload = await resizeImage(file, config.maxWidth, config.maxHeight);
    }

    // Generate unique filename
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2);
    const extension = file.name.split('.').pop();
    const filename = `${timestamp}_${randomString}.${extension}`;

    // Create storage reference
    const path = customPath 
      ? `${config.path}/${customPath}/${filename}`
      : `${config.path}/${userId}/${filename}`;
    
    const storageRef = ref(storage, path);

    // Upload file
    const snapshot = await uploadBytes(storageRef, fileToUpload);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      path: path,
      filename: filename,
      size: fileToUpload.size,
      type: fileToUpload.type,
      uploadedAt: new Date()
    };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

// Upload multiple files
export const uploadMultipleFiles = async (files, configKey, userId, customPath = '') => {
  try {
    const uploadPromises = Array.from(files).map(file => 
      uploadFile(file, configKey, userId, customPath)
    );

    const results = await Promise.all(uploadPromises);
    return results;
  } catch (error) {
    console.error('Error uploading multiple files:', error);
    throw error;
  }
};

// Delete file from Firebase Storage
export const deleteFile = async (filePath) => {
  try {
    const storageRef = ref(storage, filePath);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

// Upload profile image
export const uploadProfileImage = async (file, userId) => {
  return uploadFile(file, 'PROFILE_IMAGES', userId);
};

// Upload resume
export const uploadResume = async (file, userId) => {
  return uploadFile(file, 'RESUMES', userId);
};

// Upload portfolio
export const uploadPortfolio = async (file, userId) => {
  return uploadFile(file, 'PORTFOLIOS', userId);
};

// Upload event image (admin only)
export const uploadEventImage = async (file, eventId) => {
  return uploadFile(file, 'EVENT_IMAGES', 'admin', eventId);
};

// Upload company logo
export const uploadCompanyLogo = async (file, userId) => {
  return uploadFile(file, 'COMPANY_LOGOS', userId);
};

// Upload document
export const uploadDocument = async (file, userId, documentType = 'general') => {
  return uploadFile(file, 'DOCUMENTS', userId, documentType);
};

// Get file metadata
export const getFileMetadata = async (filePath) => {
  try {
    const storageRef = ref(storage, filePath);
    const metadata = await getMetadata(storageRef);
    return metadata;
  } catch (error) {
    console.error('Error getting file metadata:', error);
    throw error;
  }
};

// Create file upload component hook
export const useFileUpload = (configKey) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const upload = async (files, userId, customPath = '') => {
    setUploading(true);
    setError(null);
    setProgress(0);

    try {
      const fileArray = Array.isArray(files) ? files : [files];
      const results = [];

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const result = await uploadFile(file, configKey, userId, customPath);
        results.push(result);
        setProgress(((i + 1) / fileArray.length) * 100);
      }

      setUploadedFiles(results);
      return results;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const deleteUploadedFile = async (filePath) => {
    try {
      await deleteFile(filePath);
      setUploadedFiles(prev => prev.filter(file => file.path !== filePath));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    upload,
    deleteUploadedFile,
    uploading,
    progress,
    error,
    uploadedFiles,
    setError
  };
};
